import Box from "@mui/material/Box";
import {fetchMatterStatus} from "app/utils/constants/fetchMappingData";
import {useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {fetchCountryData} from "../../api/countries";
import {get} from "../../common/network";
import FormSearchFilter from "../../components/common/form-search-filter/FormSearchFilter";
import AssignAttorney from "../../components/common/modal-preview/AssignAttorney";
import MattersMuiTableWrapper from "../../components/common/mui-table-wrapper/MattersMuiTableWrapper";
import SidebarTagsDrawer from "../../components/common/sidebar-tags-wrapper/SidebarTagsDrawer";
import SidebarTagsWrapper from "../../components/common/sidebar-tags-wrapper/SidebarTagsWrapper";
import MultiFilter from "../../components/multi-filter/Multifilter";
import {PriorApplicationStatusOptions} from "../../constants";
import {DateFormatter, formatDate} from "../../constants/date_format";
import {normalizeDate} from "../../utils/CommonMethods";
import MatterDetailsModal from "../matters/matter-details/MatterDetailsModal";


const MattersListing = () => {
    const [selectedRows, setSelectedRows] = useState([]); // Array of the selected rows
    const [isMatterDetailsOpen, setIsMatterDetailsOpen] = useState(false);
    const [isRowId, setIsRowId] = useState(null);
    const [combinedData, setCombinedData] = useState({});
    const [data, setData] = useState([]);
    const [isFetching, setIsFetching] = useState(true);
    const [filterText, setFilterText] = useState("");
    const [searchValue, setSearchValue] = useState("");
    const [formTypeValue, setFormTypeValue] = useState("");
    const [dateValue, setDateValue] = useState("");
    const [matterStatusMapping, setMatterStatusMapping] = useState({});
    const [rowsSelection, setRowsSelection] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [totalCount, setTotalCount] = useState(0);
    const [sortedColumn, setSortedColumn] = useState("updated_at");
    const [sortDirection, setSortDirection] = useState("desc");
    const [countriesMapping, setCountriesMapping] = useState([]);
    const [isAssignAttorneyModalOpen, setIsAssignAttorneyModalOpen] = useState(false);
    const [dynamicFilters, setDynamicFilters] = useState([
        { column: '', operator: '', value: [], from: null, to: null }
    ]);
    const [searchResetKey, setSearchResetKey] = useState(0);
    const [filteredData, setFilteredData] = useState([]);
    const navigate = useNavigate();
    const location = useLocation();

    const selectedRowsFromState = location.state?.selectedRows || [];
    const [selectedRowsLower, setSelectedRowsLower] = useState(selectedRowsFromState);

    const multiFilterColumns = [
        {
            field: "host_file_number",
            headerName: "Docket No.",
            type: "text",
        },
        {
            field: "application_number",
            headerName: "Application No.",
            type: "text",
        },
        {
            field: "status",
            headerName: "Status",
            type: "select",
            options: PriorApplicationStatusOptions,
        },
        {
            field: "filing_date",
            headerName: "Filing Date",
            type: "date",
        },
        {
            field: "grant_date",
            headerName: "Issue Date",
            type: "date",
        },
        {
            field: "updated_at",
            headerName: "Imported On",
            type: "date",
        },
        {
            field: "client",
            headerName: "Client",
            type: "text",
        },
        {
            field: "applicant",
            headerName: "Applicant",
            type: "text",
        },
        {
            field: "country_code",
            headerName: "Country",
            type: "select",
            options: countriesMapping,
        },
    ];
    useEffect(() => {
        const combinedDataFunction = selectedRows?.flatMap(matter =>
            selectedRowsLower.map(form => ({
                matter_id: matter.id,
                form_id: form.id
            }))
        );
        setCombinedData(combinedDataFunction);
    }, [selectedRows, selectedRowsLower]);

    useEffect(() => {
        if (!selectedRowsLower?.length) {
            navigate(-1);
        }
    }, [selectedRowsLower]);

    useEffect(() => {
        setFilteredData(data);
    }, [data]);


    //#region for Column integration
    const columns = [
        {
            field: "host_file_number",
            header: "Docket No.",
            render: (value, row) => {
                return (
                    <div
                        style={{textDecoration: "underline", cursor: "pointer", paddingTop: "1%", paddingBottom: "1%"}}
                        onClick={() => {
                            setIsMatterDetailsOpen(true);
                            setIsRowId(row?.id);
                        }}
                    >
                        {value}
                    </div>
                );
            }
        },
        {
            field: "application_number",
            header: "Application No."
        },
        {
            field: "status",
            header: "Status",
            render: value => matterStatusMapping[value] || value
        },
        {
            field: "filing_date",
            header: "Filing Date",
            render: value => {
                if (!value) return null;

                return formatDate(value, true);
            }
        },
        {
            field: "grant_date",
            header: "Issue Date",
            render: value => {
                if (!value) return null;

                return formatDate(value, true);
            }
        },
        {
            field: "updated_at",
            header: "Imported On",
            render: value => {
                const date = new Date(value); // Convert the ISO date string to a Date object
                return DateFormatter(date);
            }
        },
        {
            field: "client",
            header: "Client",
            render: value => value || ""
        },
        {
            field: 'applicant',
            header: 'Applicant',
            render: value => value || ""
        },
        {
            field: "country_code",
            header: "Country",
            render: value => value || ""
        }
    ];

    const handleApplyFilters = (filters) => {
        // Clear single search state
        setSearchValue("");
        setFilterText("");
        setFormTypeValue("");
        setDateValue("");
        setSearchResetKey(prev => prev + 1);
        
        // Normalize and store filters
        const normalizedFilters = filters.map(f => ({
            ...f,
            from: normalizeDate(f.from),
            to: normalizeDate(f.to)
        }));
        
        setDynamicFilters(normalizedFilters);
        setPage(0);
        
        // Pass filters to fetchData
        fetchData({
            nextPage: 0,
            nextDynamicFilters: normalizedFilters
        });
    };

    const handleSearch = value => {
        setDynamicFilters([{ column: '', operator: '', value: [], from: null, to: null }]);
        const columnName = value?.value || ""; // Name of the column
        const searchValue = value?.searchedText || "";
        const dropdownValue = value?.dropdownValue || "";
        setSearchValue(searchValue);
        setFilterText(columnName);
        setFormTypeValue(dropdownValue);

        if (["updated_at", "filing_date", "grant_date"].includes(columnName)) {
            setDateValue(value);
        }
        setPage(0);
        fetchData({
            nextPage: 0,
            nextRowsPerPage: rowsPerPage,
            nextSortedColumn: sortedColumn,
            nextSortDirection: sortDirection,
            nextSearchValue: searchValue,
            nextFilterColumn: columnName,
            nextFormTypeValue: dropdownValue,
            nextDateValue: value
        });
    };

    const buildQueryParams = ({
        nextPage = page,
        nextRowsPerPage = rowsPerPage,
        nextSortedColumn = sortedColumn,
        nextSortDirection = sortDirection,
        nextSearchValue = searchValue,
        nextFilterColumn = filterText,
        nextFormTypeValue = formTypeValue,
        nextDateValue = dateValue,
        nextDynamicFilters = dynamicFilters
    } = {}) => {
        const params = new URLSearchParams();
        params.append("page", (nextPage + 1).toString());
        params.append("pageSize", nextRowsPerPage.toString());
        
        // Filter out empty filter objects
        const activeFilters = nextDynamicFilters.filter(f => 
            f.column && (
                f.operator === 'isEmpty' || 
                f.operator === 'isNotEmpty' ||
                (f.from || f.to) ||
                (Array.isArray(f.value) && f.value.length > 0) ||
                (typeof f.value === 'string' && f.value.trim())
            )
        );
        
        params.append("filters", JSON.stringify(activeFilters));

        // Only apply single search if no multi-filters are active
        if (activeFilters.length === 0) {
        let effectiveSearch = nextSearchValue;
        if (!effectiveSearch && nextFilterColumn === "status" && nextFormTypeValue) {
            effectiveSearch = nextFormTypeValue;
        }

        if (effectiveSearch) {
            params.append("search", effectiveSearch);

            if (nextFilterColumn && nextFilterColumn !== "all") {
                params.append("searchField", nextFilterColumn);
            }
        }

        if (["updated_at", "filing_date", "grant_date"].includes(nextFilterColumn) && nextDateValue) {
            const start = nextDateValue.startDate;
            const end = nextDateValue.endDate;

            const formatDate = (d) => {
                if (!d) return null;
                const dateObj = ["filing_date", "grant_date"].includes(nextFilterColumn) ? new Date(d + "T00:00:00") : new Date(d);
                const month = `${dateObj.getMonth() + 1}`.padStart(2, "0");
                const day = `${dateObj.getDate()}`.padStart(2, "0");
                const year = dateObj.getFullYear();
                return `${month}-${day}-${year}`;
            };

            const from = formatDate(start);
            const to = formatDate(end);

            if (nextFilterColumn === "updated_at") {
                if (from) params.append("updatedAtFrom", from);
                if (to) params.append("updatedAtTo", to);
            } else if (nextFilterColumn === "filing_date") {
                if (from) params.append("filingDateFrom", from);
                if (to) params.append("filingDateTo", to);
            } else if (nextFilterColumn === "grant_date") {
                if (from) params.append("grantDateFrom", from);
                if (to) params.append("grantDateTo", to);
            }
        }

        if (nextFilterColumn === "country_code" && nextFormTypeValue) {
            params.append("countryCode", nextFormTypeValue);
            }
        }

        if (nextSortedColumn) {
            const direction = nextSortDirection === "asc" ? "ASC" : "DESC";
            params.append("sort", `${nextSortedColumn}:${direction}`);
        }

        return params.toString();
    };

    const fetchData = async (options = {}) => {
        setIsFetching(true);
        try {
            const queryString = buildQueryParams(options);
            const response = await get(`docgen/matters?${queryString}`);
            setData(response?.data || []);
            const total = response.meta?.pagination?.total ?? 0;
            const apiPageSize = response.meta?.pagination?.pageSize ?? rowsPerPage;
            const apiPage = response.meta?.pagination?.page ?? (options.nextPage ?? page) + 1;
            setTotalCount(total);
            setRowsPerPage(apiPageSize);
            setPage(apiPage - 1);
        } catch (error) {
        } finally {
            setIsFetching(false);
        }
    };

    const handleCloseAssignAttorneyModal = () => {
        setIsAssignAttorneyModalOpen(false);
    };

    const fetchCountryMapping = async () => {
        const data = await fetchCountryData();
        if (data) {
            const countries = data
                .map(country => ({
                    label: country.name,
                    value: country.code,
                }))
                .sort((a, b) => a.label.localeCompare(b.label));

            setCountriesMapping(countries);
        }
    };

    useEffect(() => {
        const getMatterStatus = async () => {
            const response = await fetchMatterStatus();
            setMatterStatusMapping(response);
            await fetchData();
        };
        fetchCountryMapping();
        getMatterStatus();
    }, []);

    return (
        <SidebarTagsWrapper
            active={true}
            component={
                <SidebarTagsDrawer
                    lowerSectionProps={{
                        title: "Forms Selected",
                        setSelectedRows: setSelectedRowsLower,
                        selectedRows: selectedRowsLower,
                        tagName: "name"
                    }}
                    upperSectionProps={{
                        title: "Docket Selected",
                        setSelectedRows: setSelectedRows,
                        selectedRows: selectedRows,
                        tagName: "doc_no"
                    }}
                    upperSubSectionProps={ selectedRows.length > 0 && {
                        title: "Assign Attorney",
                        setIsAssignAttorneyModalOpen: setIsAssignAttorneyModalOpen
                    }}
                    buttonProps={{
                        title: "Generate Preview",
                        disabled: selectedRows.length === 0,
                        onClick: () => {
                            navigate("/docgen/forms/preview", {
                                state: {
                                    selectedRows: selectedRowsLower,
                                    selectedRowsFromState: selectedRows,
                                    combinedData
                                }
                            });
                        }
                    }}
                />
            }
        >
            <div className="page-content-wrapper">
                <Box className="w-100">
                    <header className="section-heading">
                        <div className="left-area">
                            <FormSearchFilter
                                options={[
                                    {value: "all", label: "All"},
                                    {value: "host_file_number", label: "Docket No."},
                                    {value: "application_number", label: "Application No."},
                                    {value: "status", label: "Status"},
                                    {value: "updated_at", label: "Imported On"},
                                    {value: "filing_date", label: "Filing Date"},
                                    {value: "grant_date", label: "Issue Date"},
                                    {value: "client", label: "Client"},
                                    {value: "applicant", label: "Applicant"},
                                    {value: "country_code", label: "Country"}
                                ]}
                                defaultOption={{value: "", label: "Select an option"}}
                                dropdownArray={["status", "country_code"]}
                                dropdownOptions={{
                                    status: [
                                        {
                                            value: "Abandoned",
                                            label: "Abandoned"
                                        },
                                        {
                                            value: "Allowed",
                                            label: "Allowed"
                                        },

                                        {
                                            value: "Expired",
                                            label: "Expired"
                                        },
                                        {
                                            value: "Issued",
                                            label: "Issued"
                                        },
                                        {
                                            value: "Pending",
                                            label: "Pending"
                                        },
                                        {
                                            value: "Published",
                                            label: "Published"
                                        },
                                        {
                                            value: "Reissued",
                                            label: "Reissued"
                                        },
                                        {
                                            value: "Transferred",
                                            label: "Transferred"
                                        },
                                        {
                                            value: "Unfiled",
                                            label: "Unfiled"
                                        }
                                    ],
                                    country_code: countriesMapping
                                }}
                                dateFiltersOptionsValueArray={["updated_at", "filing_date", "grant_date"]}
                                onSearch={(values) => handleSearch(values)}
                                searchResetKey={searchResetKey}
                            />
                        </div>
                        <div className="right-area">
                            <section className="top-actions-section">
                                <div className="buttons-group">
                                    <MultiFilter 
                                        filters={dynamicFilters}
                                        setFilters={setDynamicFilters}
                                        columns={multiFilterColumns}
                                        onAddFilter={handleApplyFilters}
                                    />
                                </div>
                            </section>
                        </div>
                    </header>
                    <MattersMuiTableWrapper
                        columns={columns}
                        selectedRows={selectedRows}
                        setSelectedRows={setSelectedRows}
                        colgroup={[
                            {width: "11%"},
                            {width: "11%"},
                            {width: "11%"},
                            {width: "11%"},
                            {width: "11%"},
                            {width: "11%"},
                            {width: "15%"},
                            {width: "15%"}
                        ]}
                        data={filteredData}
                        isFetching={isFetching}
                        rowsSelection={rowsSelection}
                        setRowsSelection={setRowsSelection}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        totalCount={totalCount}
                        sortedColumn={sortedColumn}
                        sortDirection={sortDirection}
                        onPageChange={(event, newPage) => {
                            setPage(newPage);
                            fetchData({
                                nextPage: newPage,
                                nextDynamicFilters: dynamicFilters
                            });
                        }}
                        onRowsPerPageChange={(event) => {
                            const value = event.target?.value ?? 25;
                            setRowsPerPage(value);
                            setPage(0);
                            fetchData({
                                nextPage: 0,
                                nextRowsPerPage: value,
                                nextDynamicFilters: dynamicFilters
                            });
                        }}
                        onSortChange={(column) => {
                            if (column === sortedColumn) {
                                const nextDirection = sortDirection === "asc" ? "desc" : "asc";
                                setSortDirection(nextDirection);
                                fetchData({
                                    nextSortDirection: nextDirection,
                                    nextSortedColumn: column,
                                    nextDynamicFilters: dynamicFilters
                                });
                            } else {
                                setSortedColumn(column);
                                setSortDirection("asc");
                                fetchData({
                                    nextSortedColumn: column,
                                    nextSortDirection: "asc",
                                    nextDynamicFilters: dynamicFilters
                                });
                            }
                        }}
                    />
                </Box>
            </div>
            <MatterDetailsModal open={isMatterDetailsOpen} handleClose={() => setIsMatterDetailsOpen(false)} id={isRowId}/>
            <AssignAttorney
                openModal={isAssignAttorneyModalOpen}
                closeModal={handleCloseAssignAttorneyModal}
                selectedDockets={selectedRows}
                setSelectedDockets={setSelectedRows}
            />
        </SidebarTagsWrapper>
    );
};


export default MattersListing;